// Iter79 — Generic Service Request modal used by the new SR types
// (CMM measurement, 3D scanning, material testing, extra loading/unloading,
// and the transport sub-types). All these types are stored on the same
// `service_requests` collection via POST /api/service-requests with a free
// `type` string. The form is intentionally compact: title, description,
// assignee tenant (optional), priority, due_date, optional incident link.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useTenants } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_META = {
  "cmm_measurement":   { label: "CMM Measurement",  desc: "Coordinate measuring machine inspection" },
  "scanning_3d":       { label: "3D Scanning",      desc: "Optical / structured-light 3D scan" },
  "3d_scanning":       { label: "3D Scanning",      desc: "Optical / structured-light 3D scan" },
  "material_testing":  { label: "Material Testing", desc: "Lab / mechanical / chemical test" },
  "transport_standard":{ label: "Standard Transport", desc: "Routine carrier / fleet movement" },
  "transport_sonderfahrt": { label: "Special Transport (Sonderfahrt)", desc: "Express / dedicated carrier" },
  "extra_loading":     { label: "Extra Loading",    desc: "Additional pickup / loading slot" },
  "extra_unloading":   { label: "Extra Unloading",  desc: "Additional drop-off / unloading slot" },
};

const inputStyle = {
  background: "var(--bg3, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize: 12,
  width: "100%", padding: "6px 8px", borderRadius: 4, outline: "none",
};

const mono = (size, color = "#9198b0", weight = 400, ls = "1.2px") => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

const Field = ({ label, required, children }) => (
  <div className="mt-3">
    <div style={mono(9)} className="uppercase mb-1">{label}{required && " *"}</div>
    {children}
  </div>
);

export const GenericServiceRequestModal = ({ type, onClose, onCreated }) => {
  const meta = TYPE_META[type] || { label: type, desc: "Service request" };
  const addToast = useStore((s) => s.addToast);
  const { data: tenants = [] } = useTenants();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeTenantId, setAssigneeTenantId] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("standalone");
  const [incidents, setIncidents] = useState([]);
  const [search, setSearch] = useState("");
  const [pickedIncident, setPickedIncident] = useState(null);

  useEffect(() => {
    if (mode !== "linked") return;
    axios.get(`${API}/incidents?status_in=open,investigating,escalated`)
      .then(({ data }) => setIncidents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [mode]);

  const filteredIncidents = useMemo(() => {
    const s = search.toLowerCase();
    return incidents.filter((i) => !s || (i.id || "").toLowerCase().includes(s) || (i.title || "").toLowerCase().includes(s)).slice(0, 30);
  }, [incidents, search]);

  const submit = async () => {
    setError("");
    if (!title.trim()) { setError("Title is required"); return; }
    if (mode === "linked" && !pickedIncident) { setError("Pick an incident or switch to standalone"); return; }
    setBusy(true);
    try {
      const payload = {
        type,
        title: title.trim(),
        description: description.trim() || null,
        assignee_tenant_id: assigneeTenantId || null,
        priority,
        due_date: dueDate || null,
        incident_id: mode === "linked" ? pickedIncident.id : null,
      };
      const { data } = await axios.post(`${API}/service-requests`, payload);
      addToast({ type: "success", title: `${meta.label} created`, body: data.id });
      onCreated?.(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Save failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-3"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
      onClick={onClose}
      data-testid="generic-sr-modal-overlay"
    >
      <div
        className="rounded-md p-4"
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.18)",
          width: "min(620px, 96vw)", maxHeight: "92vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="generic-sr-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)" }}>
            New {meta.label}
          </span>
          <button onClick={onClose} style={{ color: "#9aa1b3", fontSize: 22, cursor: "pointer", background: "transparent", border: "none" }} data-testid="generic-sr-modal-close">×</button>
        </div>
        <div style={mono(10)} className="mb-2">{meta.desc}</div>

        <div className="mb-3 flex gap-2">
          <button type="button" onClick={() => setMode("standalone")}
            className="px-3 py-1.5 rounded"
            style={{ background: mode === "standalone" ? "rgba(24,95,165,0.16)" : "transparent",
                     border: `1px solid ${mode === "standalone" ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                     color: mode === "standalone" ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
                     ...mono(11), cursor: "pointer" }}
            data-testid="generic-sr-mode-standalone">Standalone</button>
          <button type="button" onClick={() => setMode("linked")}
            className="px-3 py-1.5 rounded"
            style={{ background: mode === "linked" ? "rgba(24,95,165,0.16)" : "transparent",
                     border: `1px solid ${mode === "linked" ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                     color: mode === "linked" ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
                     ...mono(11), cursor: "pointer" }}
            data-testid="generic-sr-mode-linked">Link to incident</button>
        </div>

        {mode === "linked" && (
          <div className="mb-3 rounded-md p-2" style={{ border: "1px solid rgba(255,255,255,0.10)" }}>
            <input type="text" placeholder="Search incidents…" value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, marginBottom: 6 }} data-testid="generic-sr-incident-search" />
            <div className="max-h-40 overflow-y-auto space-y-1">
              {filteredIncidents.map((inc) => (
                <button key={inc.id} type="button" onClick={() => setPickedIncident(inc)}
                  className="w-full text-left px-2 py-1.5 rounded"
                  style={{ background: pickedIncident?.id === inc.id ? "rgba(24,95,165,0.20)" : "var(--bg-base, #111420)",
                           border: pickedIncident?.id === inc.id ? "1px solid rgba(24,95,165,0.55)" : "1px solid rgba(255,255,255,0.08)",
                           cursor: "pointer" }}>
                  <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9ec3ec" }}>{inc.id}</span>
                  <span className="ml-2" style={{ fontFamily: "Syne, sans-serif", fontSize: 12, color: "var(--text, #f0f2f8)" }}>{inc.title}</span>
                </button>
              ))}
              {filteredIncidents.length === 0 && <div style={mono(10)} className="px-1 py-1">No open incidents match.</div>}
            </div>
          </div>
        )}

        <Field label="Title" required>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} placeholder="Short summary" data-testid="generic-sr-title" />
        </Field>
        <Field label="Description">
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inputStyle, resize: "vertical" }} placeholder="Optional details — what / where / how much" data-testid="generic-sr-description" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Assignee tenant (optional)">
            <select value={assigneeTenantId} onChange={(e) => setAssigneeTenantId(e.target.value)} style={inputStyle} data-testid="generic-sr-assignee">
              <option value="">— select (internal or external) —</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{`${t.name} ${t.type ? `· ${t.type}` : ""}`}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputStyle} data-testid="generic-sr-priority">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
          </Field>
          <Field label="Due date">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inputStyle} data-testid="generic-sr-due-date" />
          </Field>
        </div>

        {error && <div className="mt-3 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", ...mono(11) }} data-testid="generic-sr-error">{error}</div>}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-md"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="generic-sr-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className="px-3 py-2 rounded-md"
            style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, border: "none" }}
            data-testid="generic-sr-submit">{busy ? "Submitting…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
};
